import { useQuery } from '@tanstack/react-query';
import { useUserPlanService, type UserPlanData } from '../services/user-plan.service';

export interface UserPlan {
    planData: UserPlanData | undefined;
    isLoadingPlan: boolean;
    hasAccess: () => boolean;
    hasEnoughTransactions: (requiredTransactions: number) => boolean;
    isExpired: () => boolean;
    isUnlimited: () => boolean;
    getRemainingTransactions: () => number;
    getTransactionLimit: () => number | null;
    getCurrentTransactions: () => number;
    isTaxReportsEnabled: () => boolean;
}

/**
 * Hook to get user plan from CoinTracking main app (via BetaApp API)
 * Uses GLOBAL transaction limits (not per-year)
 * 
 * Key Differences from Old System:
 * - OLD: 200 transactions per year (2023: 200, 2024: 200 = 400 total)
 * - NEW: 3,500 transactions TOTAL across ALL years
 */
export const useUserPlan = (): UserPlan => {
    const { getUserPlans } = useUserPlanService();

    const { data: planData, isLoading: isLoadingPlan } = useQuery({
        queryKey: ['userPlan'],
        queryFn: getUserPlans,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    // Debug logging
    console.log('[useUserPlan] Current state:', {
        planData,
        isLoadingPlan,
        taxReportsEnabled: planData?.plan?.taxReportsEnabled
    });

    /**
     * Check if user has any access (not expired and has some limit)
     */
    const hasAccess = (): boolean => {
        if (!planData?.plan) return false;
        
        const { isExpired, isUnlimited, transactionLimit } = planData.plan;
        
        // Expired plans have no access
        if (isExpired) return false;
        
        // Unlimited plans always have access
        if (isUnlimited) return true;
        
        // Check if has any transaction limit
        return (transactionLimit ?? 0) > 0;
    };

    /**
     * Check if user has enough transactions globally
     * @param requiredTransactions - Total transactions needed across ALL years
     */
    const hasEnoughTransactions = (requiredTransactions: number): boolean => {
        if (!planData?.plan) return false;
        
        const { isUnlimited, transactionLimit, currentTransactions } = planData.plan;
        
        // Unlimited plans always have enough
        if (isUnlimited) return true;
        
        // Check if limit is sufficient
        const limit = transactionLimit ?? 0;
        return limit >= requiredTransactions && currentTransactions < limit;
    };

    /**
     * Check if plan is expired
     */
    const isExpired = (): boolean => {
        return planData?.plan?.isExpired ?? true;
    };

    /**
     * Check if plan is unlimited
     */
    const isUnlimited = (): boolean => {
        return planData?.plan?.isUnlimited ?? false;
    };

    /**
     * Get remaining transactions available
     */
    const getRemainingTransactions = (): number => {
        if (!planData?.plan) return 0;
        
        const { isUnlimited, transactionLimit, currentTransactions } = planData.plan;
        
        // Unlimited plans return a large number
        if (isUnlimited) return 999999;
        
        const limit = transactionLimit ?? 0;
        const remaining = limit - currentTransactions;
        
        return Math.max(0, remaining);
    };

    /**
     * Get transaction limit (null if unlimited)
     */
    const getTransactionLimit = (): number | null => {
        return planData?.plan?.transactionLimit ?? 0;
    };

    /**
     * Get current transaction count
     */
    const getCurrentTransactions = (): number => {
        return planData?.plan?.currentTransactions ?? 0;
    };

    /**
     * Check if tax reports are enabled
     * This is calculated server-side based on:
     * - Plan status > 1 (paid plan)
     * - Within transaction limit (currentTransactions < transactionLimit OR unlimited)
     */
    const isTaxReportsEnabled = (): boolean => {
        return planData?.plan?.taxReportsEnabled ?? false;
    };

    return {
        planData,
        isLoadingPlan,
        hasAccess,
        hasEnoughTransactions,
        isExpired,
        isUnlimited,
        getRemainingTransactions,
        getTransactionLimit,
        getCurrentTransactions,
        isTaxReportsEnabled,
    };
};
